import React, { useState, useContext } from "react";
import styled from "@emotion/styled";
import { hot } from "react-hot-loader/root";
import { Global, css } from "@emotion/core";
import { range } from "d3-array";
import theme from "~/scripts/theme";

import * as S from "./Explainer.styles.js";
import { is1200Query } from "~/core/scripts/mediaquery-helper";
import { TranslationContext } from "~/kits/general/scripts/app.js";

import Board from "~/components/Board";
import Elector from "~/components/Elector";
import ResultBoard from "~/components/ResultBoard";
import ElectionGrid from "~/components/Board/layers/ElectionGrid";
import DistrictsOverlay from "~/components/Board/layers/DistrictsOverlay";

const CenterOnDesktop = styled.div`
  ${(p) => p.theme.breakpoints.sm.up} {
    text-align: center;
  }
`;

const Explainer = () => {
  const polyglot = useContext(TranslationContext);
  const [storyIndex, setStoryIndex] = useState(0);
  const isLargeDesktop = is1200Query();

  const items = [
    {
      content: (
        <div dangerouslySetInnerHTML={{ __html: polyglot.t("explainer1") }} />
      ),
    },
    {
      content: (
        <>
          <div
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainer21") }}
          />
          <S.CenterOnMobile>
            {range(3).map((i) => (
              <Elector
                key={`elector-${i}`}
                color={theme.colors.black}
                number={i + 1}
              />
            ))}
          </S.CenterOnMobile>
          <div
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainer22") }}
          />
        </>
      ),
    },
    {
      content: (
        <>
          <div
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainer31") }}
          />
          <S.CenterOnMobile>
            <ResultBoard
              background="#FFFFFF"
              trumpOverallVotes={1}
              bidenOverallVotes={2}
              trumpWon={false}
              bidenWon={false}
            />
          </S.CenterOnMobile>
          <div
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainer32") }}
          />
          <S.CenterOnMobile>
            {range(3).map((i) => (
              <Elector
                key={`elector-${i}`}
                color={
                  i < 2 ? theme.colors.democratic : theme.colors.republican
                }
                number={i + 1}
              />
            ))}
          </S.CenterOnMobile>
          <div
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainer33") }}
          />
        </>
      ),
    },
  ];

  return (
    <S.Wrapper>
      <Global
        styles={css`
          ._republican_,
          ._elephant_ {
            color: ${theme.colors.republican};
            font-weight: bold;
          }
          ._democratic_,
          ._donkey_ {
            color: ${theme.colors.democratic};
            font-weight: bold;
          }

          ._elephant_,
          ._donkey_ {
            position: relative;

            &:after {
              content: "";
              position: absolute;
              display: block;
              width: 40px;
              height: 40px;
              background-size: contain;
              background-repeat: no-repeat;
              z-index: -1;
              left: 90%;
              top: 0;
            }
          }
          ._elephant_::after {
            background-image: url("images/parties/logo--rep.png");
            transform: translateX(-60%) translateY(-60%) rotate(-10deg);
          }
          ._donkey_::after {
            background-image: url("images/parties/logo--dem.png");
            transform: translateY(-80%) translateX(-60%) rotate(-10deg);
            left: 0%;
            top: 100%;
          }

          .deutsche-version ._donkey_::after {
            left: 90%;
            top: 90%;
            transform: translateX(-35%) translateY(-25%) rotate(10deg);

            @media screen and (min-width: 400px) {
              left: 90%;
              top: 0;
              transform: translateX(-60%) translateY(-50%) rotate(10deg);
            }
          }
          .english-version ._donkey_::after {
            left: 90%;
            top: 0;
            transform: translateX(-60%) translateY(-50%) rotate(10deg);
          }

          .headline .title {
            font-family: Roboto;
            font-weight: 700;
            text-shadow: -2px 2px 2px rgba(255, 255, 255, 0.5),
              -2px -2px 2px rgba(255, 255, 255, 0.5),
              2px -2px 2px rgba(255, 255, 255, 0.5),
              2px 2px 2px rgba(255, 255, 255, 0.5);
            margin: 15px auto 12px 0;
            font-weight: 700;
            font-size: 1.8rem;

            @media screen and (min-width: 375px) {
              font-size: 2rem;
              max-width: 100%;
            }

            @media screen and (min-width: 480px) {
              font-size: 2.1rem;
            }

            @media screen and (min-width: 768px) {
              max-width: 12em !important;
              margin-left: auto;
              font-size: 3.6rem;
              ._donkey_::after {
                transform: translateY(-80%) translateX(-60%) rotate(-10deg);
                left: 0%;
                top: 100%;
              }
            }

            span {
              position: relative;
              font-weight: 700 !important;
            }
          }
        `}
      />
      <CenterOnDesktop>
        <div className="text-wrap">
          <h3
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainerTitle") }}
          />
        </div>
      </CenterOnDesktop>
      <S.InnerWrapper>
        <S.StickyWrapper>
          <Board>
            <ElectionGrid small={!isLargeDesktop} />
            {storyIndex > 0 && (
              <DistrictsOverlay
                isFilled={storyIndex === 2}
                small={!isLargeDesktop}
              />
            )}
          </Board>
        </S.StickyWrapper>
        <S.StoryItems>
          {items.map((item, i) => (
            <S.StoryItem
              key={`story-item-${i}`}
              onEnterViewport={() => setStoryIndex(i)}
              isActive={storyIndex === i}
            >
              {item.content}
            </S.StoryItem>
          ))}
        </S.StoryItems>
      </S.InnerWrapper>
    </S.Wrapper>
  );
};

export default hot(Explainer);
